/**
 * `.plan` — the authored body plan, and the second file format in this project.
 *
 * ==== WHY A FILE AND NOT A LITERAL ======================================
 *
 * `docs/25-pixel-tools.md` §2 decides what a character editor in this repo can
 * possibly be. A body is rasterised fresh in every pose, at every size, for
 * every archetype, so there is no sprite to paint; and a per-pose override is
 * authored against one `(plan, halfH, pose, phase)`, all four of which move — a
 * re-proportioning like #58 would have turned every override *wrong* rather
 * than missing. What is left, and what §2 chooses, is that **the editable thing
 * is the plan**: a declared height and hip, and a list of bones with a parent, a
 * length, two radii, a rest angle, a tissue, a role and a layer.
 *
 * Those numbers lived as a TypeScript literal, which is a fine way to *hold*
 * them and a poor way to *edit* them: a tool that moves a bone has to rewrite
 * source, and nothing can then promise the rewrite was lossless. So a plan is a
 * text file, and this module is its parser, its serialiser and its resolver —
 * the same division `src/sim/shapeMask.ts` makes for `.pxm`, deliberately, so
 * that there is one format discipline in this project rather than two.
 *
 * ==== THE ROUND-TRIP CONTRACT ===========================================
 *
 * **`serializeBodyPlan(parseBodyPlan(t)) === t`, byte for byte.** The failure
 * mode of a lossy asset format is silent and cumulative — a dropped comment, a
 * reordered field, a number quietly re-rendered — and nobody diffs a file they
 * only meant to nudge a bone in. So the format has **exactly one spelling of
 * any plan** and the parser *rejects* the near-misses instead of normalising
 * them, which makes the round trip a property of the format rather than of the
 * serialiser being careful. `tests/bodyPlanText.test.ts` §3 lists the rejected
 * spellings and asserts that each would have broken byte-identity had it been
 * accepted.
 *
 * Two consequences worth stating, because they look like pedantry until the
 * reason is written down:
 *
 *  - **Every number is in JavaScript's own shortest round-tripping form**, the
 *    one `String(n)` produces. `length 5.0` is rejected in favour of
 *    `length 5`, and `r0 2.10` in favour of `r0 2.1`, because otherwise a file
 *    edited by the tool comes back spelled differently from the file the author
 *    wrote and every save churns the diff. `Number(String(n)) === n` is
 *    guaranteed by the language, so this costs no precision at all.
 *  - **An angle names a direction and an offset**, `DOWN-0.3` rather than
 *    `1.2707963267948965`. This is the one place the format is *more* than a
 *    transcription of the literal, and it is worth it twice over: the literal
 *    was written `DOWN - 0.30` because that is what an author means, and
 *    sixteen significant figures of a radian is not a number anyone can edit or
 *    review. `DIR + off` is the same IEEE operation the source performed
 *    (`a - b` *is* `a + (-b)`, exactly), so the authored file poses
 *    bit-identically to the literal it replaced — `tests/bodyPlanText.test.ts`
 *    §2 is that claim, checked while both descriptions still exist.
 *
 * Comments are inside the contract, and not only at the head of the file. The
 * humanoid's arm angles carry eighty lines of measurement explaining why they
 * are what they are, attached to the bone they justify; a format that could
 * only keep a file-level preamble would have forced all of it into one block
 * where the next person to move an arm will not read it. So a `#` block
 * immediately above a `bone` line belongs to that bone and travels with it.
 *
 * ==== WHY THE SERIALISER LIVES HERE, WITH NO RUNTIME CALLER =============
 *
 * `parseBodyPlan` is called by `pixelBody.ts`; `serializeBodyPlan` is called
 * only by `tools/pixel.mjs` and by the tests. It stays here anyway, because the
 * alternative is a second implementation of the format inside the tool — and
 * then the round-trip test proves that two programs agree rather than that the
 * format is lossless. One writer, one reader, one file.
 *
 * ==== WHAT SHIPS ========================================================
 *
 * The `.plan` files are pulled in with Vite's `?raw`, so they are inlined into
 * the bundle as string literals at build time: no fetch, no asset directory, no
 * new dependency.
 *
 * Tissue is carried **by name** and resolved by a function the caller passes
 * in, which is the shape `specFor` uses for materials and is here for two of
 * the same three reasons: a document that carried an ordinal would silently
 * mean something else the moment the enum grew, and a plan naming a tissue the
 * build does not have fails at resolution with the plan's name attached rather
 * than posing a bone into slot `undefined`. The third reason is this module's
 * own: taking the table as an argument keeps the import of `pixelBody.ts`
 * type-only, and a `.plan` parser that imported a *value* from the module that
 * imports it would be a load-order cycle in which `ANGLE_DIRS` is still in its
 * temporal dead zone when the first plan is parsed.
 */

import type { Bone, BodyPlan, TissueId } from '../pixelBody';

/**
 * The rest-angle directions, in the convention `poseSkeleton` uses: radians,
 * `0` pointing along +x, +y downward.
 *
 * Four now: `LEFT` joined for issue #186's quadruped tail, which is the first
 * bone in the roster that genuinely points backwards — every plan rasterises
 * facing `+x` (`poseSkeleton`'s "Always rasterise facing RIGHT"), so a limb
 * trailing behind the body points `-x`, and `RIGHT+off`/`RIGHT-off` cannot
 * spell that without going through a full turn.
 */
export const ANGLE_DIRS: Readonly<Record<string, number>> = {
  RIGHT: 0,
  DOWN: Math.PI / 2,
  UP: -Math.PI / 2,
  LEFT: Math.PI,
};

/**
 * Every legal `role`, which is also every legal bone *name* — see `PlanBoneDoc`.
 *
 * A `Record` keyed by the union rather than an array of it, because that is the
 * spelling the compiler checks in **both** directions: a role added to
 * `Bone['role']` and not to this table is a missing-property error, and a
 * typo here is an excess-property error. An array of strings would have been
 * assignable while silently short.
 */
const ROLES: Readonly<Record<Bone['role'], true>> = {
  torso: true, neck: true, head: true, legL: true, legR: true,
  armL: true, armR: true, tail: true, blob: true,
  legLowL: true, legLowR: true, armLowL: true, armLowR: true,
  tailLow: true, tailTip: true,
};

export const PLAN_ROLES: readonly string[] = Object.keys(ROLES);

/**
 * An angle as the file spells it: a named direction plus a signed offset.
 *
 * Kept apart rather than collapsed to a number because the round trip is on the
 * *authored* form. `{ dir: 'DOWN', off: -0.3 }` and a bare `1.2707963267948965`
 * are the same radian and only one of them is a thing a person can edit.
 */
export interface PlanAngle {
  readonly dir: string;
  /** Zero when the bone points exactly along `dir`. */
  readonly off: number;
}

/** One bone, in the exact structure the file spells. */
export interface PlanBoneDoc {
  /**
   * The `#` lines immediately above this bone, verbatim and including the `#`.
   *
   * Where the argument for a proportion lives. See the module header.
   */
  readonly comment: readonly string[];
  /**
   * The bone's `role`, which doubles as its name — `parent` refers to it and
   * `pixel plan set --bone legL.length=8.4` addresses it.
   *
   * That is a real restriction: a plan may not carry two bones with the same
   * role, and the parser says so. It is the right restriction for the plans
   * that exist (all five spell distinct roles) and it buys the thing an editor
   * needs most — a name for a bone that survives the list being reordered,
   * which an index does not. A plan that genuinely needs two tails needs a
   * second role name, and that is a smaller change than a second naming scheme.
   */
  readonly role: Bone['role'];
  /** The parent bone's role, or `null` for the root. */
  readonly parent: string | null;
  readonly length: number;
  readonly r0: number;
  readonly r1: number;
  readonly angle: PlanAngle;
  /** Tissue slot by name — `CORE`, `SHELL`, `ACCENT`. Resolved by `planFromDoc`. */
  readonly tissue: string;
  readonly layer: number;
}

/** A parsed `.plan`. */
export interface BodyPlanDoc {
  /** Leading `#` comment lines, verbatim and including the `#`. */
  readonly header: readonly string[];
  readonly id: string;
  readonly height: number;
  readonly hip: number;
  /** Absent when zero — `hipX 0` is not a legal spelling of "no offset". */
  readonly hipX?: number;
  readonly bones: readonly PlanBoneDoc[];
}

class PlanError extends Error {}

function fail(where: string, line: number, msg: string): never {
  throw new PlanError(`${where}:${line}: ${msg}`);
}

/**
 * `hasOwnProperty`, applied to a plain lookup table.
 *
 * Not decoration. `TABLE[key] ?? TABLE.fallback` never fires for
 * `key === 'toString'`, because that resolves to a *function* rather than to
 * something nullish — the latent bug `profileAt` carried until
 * `tests/weaponLobe.test.ts` walked every key (docs/25 §3). Every name-keyed
 * lookup in this file goes through here, so a bone claiming `angle constructor`
 * is a parse error rather than an angle of `function Object()`.
 */
export function own<T>(table: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}

/**
 * A number in the one spelling the format allows.
 *
 * `String(Number(tok)) === tok` is the whole rule, and it is the language's own
 * shortest-round-tripping form, so it rejects `5.0`, `+5`, `05`, `5.`, `.5`,
 * `1e1` and `-0` while costing no precision. `Infinity` stringifies to itself
 * and so passes that rule; it is excluded by the finiteness test instead.
 */
function num(where: string, line: number, field: string, tok: string): number {
  const n = Number(tok);
  if (!Number.isFinite(n) || String(n) !== tok) {
    fail(where, line, `${field}: ${JSON.stringify(tok)} is not a number in canonical form`);
  }
  return n;
}

/**
 * `UP`, `DOWN-0.3`, `RIGHT+0.42`.
 *
 * Exported because `pixel plan set --bone legL.angle=DOWN-0.3` has to read the
 * same grammar the file does, and a second reader of an angle is how a format
 * grows a second spelling.
 */
export function parsePlanAngle(tok: string, where = '<plan>', line = 0): PlanAngle {
  const m = /^([A-Za-z]+)(?:([+-])(\S+))?$/.exec(tok);
  if (m === null) fail(where, line, `angle: expected DIR, DIR+n or DIR-n, got ${JSON.stringify(tok)}`);
  if (own(ANGLE_DIRS, m[1]) === undefined) {
    fail(where, line, `angle: ${JSON.stringify(m[1])} is not one of ${Object.keys(ANGLE_DIRS).join(', ')}`);
  }
  if (m[2] === undefined) return { dir: m[1], off: 0 };
  const mag = num(where, line, 'angle', m[3]);
  // `DOWN+0` and `DOWN-0` are second spellings of `DOWN`, and a negative
  // magnitude is a third spelling of the opposite sign. Both are rejected
  // rather than folded, or the round trip stops being a property of the format.
  if (mag <= 0) fail(where, line, `angle: the offset is positive and ${JSON.stringify(m[2])} carries its sign, got ${tok}`);
  return { dir: m[1], off: m[2] === '-' ? -mag : mag };
}

/** The radian an authored angle means. */
export function angleValue(a: PlanAngle): number {
  const base = own(ANGLE_DIRS, a.dir);
  if (base === undefined) throw new PlanError(`angle: unknown direction ${a.dir}`);
  return base + a.off;
}

/** Write an angle back out, in the one spelling. */
function angleText(a: PlanAngle): string {
  if (a.off === 0) return a.dir;
  return a.off < 0 ? `${a.dir}-${String(-a.off)}` : `${a.dir}+${String(a.off)}`;
}

/** The bone line's fields, in the one order the format allows. */
const BONE_FIELDS = ['parent', 'length', 'r0', 'r1', 'angle', 'tissue', 'layer'] as const;

/** The fields `pixel plan set --bone <role>.<field>=<v>` may address. */
export const EDITABLE_BONE_FIELDS: readonly string[] = ['length', 'r0', 'r1', 'angle', 'tissue', 'layer'];

/**
 * Parse a `.plan` document.
 *
 * Strict on structure and deliberately unhelpful about near-misses, for the
 * reason `parseShapeMask` is: a parser that accepts two spellings of one plan
 * cannot promise a round trip. The only tolerances are the ones a text editor
 * imposes without asking — CRLF, and a missing final newline.
 *
 * `where` names the file in error messages, because a stack trace pointing here
 * does not say which of five plans is malformed.
 */
export function parseBodyPlan(text: string, where = '<plan>'): BodyPlanDoc {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  let i = 0;
  const header: string[] = [];
  while (i < lines.length && lines[i].startsWith('#')) header.push(lines[i++]);

  if (i >= lines.length) fail(where, i + 1, 'no `plan` line');
  const pm = /^plan ([a-z][a-zA-Z0-9_]*) height (\S+) hip (\S+)(?: hipX (\S+))?$/.exec(lines[i]);
  if (pm === null) {
    fail(where, i + 1, `expected \`plan <id> height <n> hip <n> [hipX <n>]\`, got ${JSON.stringify(lines[i])}`);
  }
  const id = pm[1];
  const height = num(where, i + 1, 'height', pm[2]);
  const hip = num(where, i + 1, 'hip', pm[3]);
  let hipX: number | undefined;
  if (pm[4] !== undefined) {
    hipX = num(where, i + 1, 'hipX', pm[4]);
    // Absent and zero are the same plan — `poseSkeleton` reads `hipX ?? 0` — so
    // only one of them may be written down.
    if (hipX === 0) fail(where, i + 1, 'hipX 0 is the default; omit it rather than spelling it');
  }
  if (height <= 0) {
    fail(where, i + 1, "height must be positive — it is the sole denominator of bodyQuad's scale");
  }
  i++;

  const bones: PlanBoneDoc[] = [];
  const declared = new Set<string>();
  while (i < lines.length) {
    // Exactly one blank line before every bone stanza. Nothing else separates
    // them, so "no blank line" and "two blank lines" are not second spellings
    // of the same plan — they are errors.
    if (lines[i] !== '') {
      fail(where, i + 1, `expected exactly one blank line before a bone, got ${JSON.stringify(lines[i])}`);
    }
    i++;
    const comment: string[] = [];
    while (i < lines.length && lines[i].startsWith('#')) comment.push(lines[i++]);
    if (i >= lines.length) fail(where, i, 'a comment block with no bone under it');

    const parts = lines[i].split(' ');
    if (parts[0] !== 'bone' || parts.length !== 2 + BONE_FIELDS.length * 2) {
      fail(
        where, i + 1,
        `expected \`bone <role> ${BONE_FIELDS.map((f) => `${f} <v>`).join(' ')}\`, got ${JSON.stringify(lines[i])}`,
      );
    }
    const role = parts[1];
    if (own(ROLES, role) === undefined) fail(where, i + 1, `\`${role}\` is not a role: ${PLAN_ROLES.join(', ')}`);
    if (declared.has(role)) fail(where, i + 1, `role \`${role}\` declared twice — a role is a bone's name here`);
    const v: Record<string, string> = Object.create(null);
    for (let f = 0; f < BONE_FIELDS.length; f++) {
      const key = parts[2 + f * 2];
      if (key !== BONE_FIELDS[f]) {
        fail(where, i + 1, `expected field \`${BONE_FIELDS[f]}\` in position ${f + 1}, got \`${key}\``);
      }
      v[key] = parts[3 + f * 2];
    }

    let parent: string | null;
    if (v.parent === '-') {
      if (bones.length !== 0) fail(where, i + 1, 'a second root — a plan has exactly one, and it is the first bone');
      parent = null;
    } else {
      if (bones.length === 0) fail(where, i + 1, 'the first bone is the root and its parent is `-`');
      // Parent-first, by name. `poseSkeleton` walks the array in order and a
      // child posed before its parent reads a joint from the previous frame.
      if (!declared.has(v.parent)) fail(where, i + 1, `parent \`${v.parent}\` is not a bone declared above this one`);
      parent = v.parent;
    }

    const length = num(where, i + 1, 'length', v.length);
    const r0 = num(where, i + 1, 'r0', v.r0);
    const r1 = num(where, i + 1, 'r1', v.r1);
    if (length <= 0) fail(where, i + 1, 'length must be positive');
    if (r0 <= 0 || r1 <= 0) fail(where, i + 1, 'r0 and r1 must be positive — a bone of no thickness draws nothing');
    const angle = parsePlanAngle(v.angle, where, i + 1);
    if (!/^[A-Z][A-Z0-9_]*$/.test(v.tissue)) fail(where, i + 1, `tissue: ${JSON.stringify(v.tissue)} is not a name`);
    const layer = num(where, i + 1, 'layer', v.layer);
    // `Skeleton.layer` is a `Uint8Array`, so a layer outside this range is not a
    // taste question: it wraps, and the body draws in an order nobody wrote.
    if (!Number.isInteger(layer) || layer < 0 || layer > 255) {
      fail(where, i + 1, 'layer must be an integer in 0..255 — Skeleton.layer is a Uint8Array');
    }

    declared.add(role);
    bones.push({
      comment, role: role as Bone['role'], parent, length, r0, r1, angle, tissue: v.tissue, layer,
    });
    i++;
  }

  if (bones.length === 0) fail(where, i, 'no bones — a plan with no skeleton is not a plan');
  return hipX === undefined
    ? { header, id, height, hip, bones }
    : { header, id, height, hip, hipX, bones };
}

/** Write a document back out. The inverse of `parseBodyPlan`, exactly. */
export function serializeBodyPlan(doc: BodyPlanDoc): string {
  const out: string[] = [...doc.header];
  const hipX = doc.hipX === undefined || doc.hipX === 0 ? '' : ` hipX ${String(doc.hipX)}`;
  out.push(`plan ${doc.id} height ${String(doc.height)} hip ${String(doc.hip)}${hipX}`);
  for (const bone of doc.bones) {
    out.push('');
    for (const line of bone.comment) out.push(line);
    out.push(
      `bone ${bone.role} parent ${bone.parent ?? '-'} length ${String(bone.length)} ` +
        `r0 ${String(bone.r0)} r1 ${String(bone.r1)} angle ${angleText(bone.angle)} ` +
        `tissue ${bone.tissue} layer ${String(bone.layer)}`,
    );
  }
  return out.join('\n') + '\n';
}

/**
 * Resolve a document into the `BodyPlan` the poser takes.
 *
 * Parent roles become indices here and nowhere else: `poseSkeleton` walks the
 * bone array in order and needs `parent` to be an index into it, while an author
 * needs a name. The parser has already established that every parent is declared
 * above its child, so this walk cannot fail.
 *
 * `resolveTissue` is passed in rather than imported — see the module header for
 * the two reasons, one of which is a load-order cycle.
 */
export function planFromDoc(
  doc: BodyPlanDoc,
  resolveTissue: (name: string) => TissueId | undefined,
): BodyPlan {
  const index = new Map<string, number>();
  const bones: Bone[] = doc.bones.map((b, i) => {
    index.set(b.role, i);
    const tissue = resolveTissue(b.tissue);
    if (tissue === undefined) throw new PlanError(`plan ${doc.id}: unknown tissue ${b.tissue} on ${b.role}`);
    return {
      parent: b.parent === null ? -1 : (index.get(b.parent) as number),
      length: b.length,
      r0: b.r0,
      r1: b.r1,
      angle: angleValue(b.angle),
      tissue,
      role: b.role,
      layer: b.layer,
    };
  });
  const plan: BodyPlan = { id: doc.id, bones, height: doc.height, hip: doc.hip };
  if (doc.hipX !== undefined && doc.hipX !== 0) plan.hipX = doc.hipX;
  return plan;
}
